import { useState } from "react";
import * as api from "../api/client";
import { pixelTheme } from "../theme/pixel";

interface Props {
  user: api.User | null;
}

/**
 * Renders a small banner when the logged-in user has a real email
 * that hasn't been verified yet. Guests (no email) don't see it.
 * Verified users don't see it. Hidden by default once the user
 * clicks "I've sent the email" — we don't have a way to know when
 * the link is actually clicked without polling, so the banner just
 * goes away on the next /api/me refresh.
 */
export function EmailVerificationBanner({ user }: Props) {
  const theme = pixelTheme;
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user || !user.email || user.email_verified || user.is_guest) {
    return null;
  }

  async function onResend() {
    if (!user || !user.email) return;
    setResending(true);
    setError(null);
    try {
      await api.resendVerification(user.email);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setResending(false);
    }
  }

  return (
    <div
      style={{
        padding: 12,
        background: theme.panel,
        border: `2px solid ${theme.accent}`,
        color: theme.ink,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        fontSize: 14,
      }}
    >
      <span style={{ flex: "1 1 240px" }}>
        Confirm your email at <strong>{user.email}</strong> to finish
        setting up your account.
      </span>
      {sent ? (
        <span style={{ color: theme.inkDim, fontSize: 13 }}>
          Sent — check your inbox.
        </span>
      ) : (
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          style={{
            background: theme.bgDeep,
            border: `2px solid ${theme.panelEdge}`,
            color: theme.ink,
            padding: "6px 12px",
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            cursor: resending ? "wait" : "pointer",
          }}
        >
          {resending ? "…" : "RESEND EMAIL"}
        </button>
      )}
      {error && (
        <span style={{ color: theme.accent, fontSize: 12 }}>{error}</span>
      )}
    </div>
  );
}
